import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { quicknoteApi, tickermapperApi, industryMapperApi, configApi, form4Api, transactionApi } from '../services/api';

/* ---- Interfaces ---- */
interface Quicknote {
  id: number; portfolio: string; ticker: string; rank: number;
  noteDate: string | null; note: string | null;
}
interface Tickermapper {
  id: number; ca_ticker: string; ca_exchange: string;
  us_ticker: string; us_exchange: string;
}
interface IndustryMapper {
  id: number; industryID: number; industryName: string;
  exchange: string; ticker: string; percentage: number;
}
interface IndustryItem { id: number; name: string; }

type TabKey = 'none' | 'notes' | 'tickermap' | 'industrymap';

const PORTFOLIO_OPTIONS = [
  'Admin_default', 'Admin_insiderTracking', 'Admin_longtermHolding',
  'Admin_preferredShares', 'Admin_selfPick', 'Admin_zdarkDays',
];
const RANK_OPTIONS = [-5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/* ---- Stock Notes Tab ---- */
const StockNotesTab: React.FC = () => {
  const [items, setItems] = useState<Quicknote[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ portfolio: '', ticker: '', rank: 0, noteDate: '', note: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ portfolio: '', ticker: '', rank: 0, noteDate: new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }), note: '' });
  const [viewId, setViewId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    quicknoteApi.list().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: Quicknote) => {
    setEditId(item.id);
    setEditForm({ portfolio: item.portfolio, ticker: item.ticker, rank: item.rank, noteDate: item.noteDate || '', note: item.note || '' });
  };
  const cancelEdit = () => setEditId(null);
  const saveEdit = async () => {
    if (editId === null) return;
    await quicknoteApi.update(editId, editForm);
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!addForm.ticker.trim()) return;
    await quicknoteApi.create(addForm);
    setAddForm({ portfolio: '', ticker: '', rank: 0, noteDate: new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }), note: '' });
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this note?')) return;
    await quicknoteApi.delete(id);
    load();
  };

  const rankColor = (rank: number) => rank > 0 ? 'green' : rank < 0 ? 'red' : 'blue';

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Note" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.formWrapper}>
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>New Stock Note</legend>
            <ol style={styles.fieldList}>
              <li style={styles.field}><label style={styles.formLabel}>Portfolio:</label>
                <select value={addForm.portfolio} onChange={e => setAddForm({ ...addForm, portfolio: e.target.value })} style={{ ...styles.input, width: '20em' }}>
                  <option value="">-- Select --</option>
                  {PORTFOLIO_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select></li>
              <li style={styles.field}><label style={styles.formLabel}>Ticker:</label>
                <input value={addForm.ticker} onChange={e => setAddForm({ ...addForm, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '10em' }} /></li>
              <li style={styles.field}><label style={styles.formLabel}>Rank:</label>
                <select value={addForm.rank} onChange={e => setAddForm({ ...addForm, rank: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '5em' }}>
                  {RANK_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select></li>
              <li style={styles.field}><label style={styles.formLabel}>Date:</label>
                <input type="date" value={addForm.noteDate} onChange={e => setAddForm({ ...addForm, noteDate: e.target.value })} style={styles.input} /></li>
              <li style={styles.field}><label style={styles.formLabel}>Note:</label>
                <textarea value={addForm.note} onChange={e => setAddForm({ ...addForm, note: e.target.value })} style={{ ...styles.input, width: '30em', height: '6em' }} /></li>
            </ol>
            <fieldset style={{ border: 'none', textAlign: 'right', padding: '0.3em 1em 1em' }}>
              <input type="button" value="Save" onClick={handleAdd} style={styles.button} />
              <input type="button" value="Cancel" onClick={() => setShowAdd(false)} style={{ ...styles.button, marginLeft: '4px' }} />
            </fieldset>
          </fieldset>
        </div>
      )}

      {/* View note detail */}
      {viewId !== null && (() => {
        const item = items.find(i => i.id === viewId);
        if (!item) return null;
        return (
          <div style={{ backgroundColor: '#f9f9f9', border: '1px solid #ccc', padding: '1em', marginBottom: '1em' }}>
            <div style={{ marginBottom: '0.3em' }}><strong>Portfolio:</strong> {item.portfolio} | <strong>Ticker:</strong> {item.ticker} | <strong>Rank:</strong> {item.rank} | <strong>Date:</strong> {item.noteDate}</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85em', maxHeight: '20em', overflow: 'auto' }}>{item.note}</div>
            <input type="button" value="Close" onClick={() => setViewId(null)} style={{ ...styles.button, marginTop: '0.5em' }} />
          </div>
        );
      })()}

      {/* Edit form */}
      {editId !== null && (
        <div style={styles.formWrapper}>
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>Edit Note #{editId}</legend>
            <ol style={styles.fieldList}>
              <li style={styles.field}><label style={styles.formLabel}>Portfolio:</label>
                <select value={editForm.portfolio} onChange={e => setEditForm({ ...editForm, portfolio: e.target.value })} style={{ ...styles.input, width: '20em' }}>
                  <option value="">-- Select --</option>
                  {PORTFOLIO_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select></li>
              <li style={styles.field}><label style={styles.formLabel}>Ticker:</label>
                <input value={editForm.ticker} onChange={e => setEditForm({ ...editForm, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '10em' }} /></li>
              <li style={styles.field}><label style={styles.formLabel}>Rank:</label>
                <select value={editForm.rank} onChange={e => setEditForm({ ...editForm, rank: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '5em' }}>
                  {RANK_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select></li>
              <li style={styles.field}><label style={styles.formLabel}>Date:</label>
                <input type="date" value={editForm.noteDate} onChange={e => setEditForm({ ...editForm, noteDate: e.target.value })} style={styles.input} /></li>
              <li style={styles.field}><label style={styles.formLabel}>Note:</label>
                <textarea value={editForm.note} onChange={e => setEditForm({ ...editForm, note: e.target.value })} style={{ ...styles.input, width: '30em', height: '10em' }} /></li>
            </ol>
            <fieldset style={{ border: 'none', textAlign: 'right', padding: '0.3em 1em 1em' }}>
              <input type="button" value="Save" onClick={saveEdit} style={styles.button} />
              <input type="button" value="Cancel" onClick={cancelEdit} style={{ ...styles.button, marginLeft: '4px' }} />
            </fieldset>
          </fieldset>
        </div>
      )}

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Portfolio</th>
            <th style={styles.th}>Ticker</th>
            <th style={styles.th}>Rank</th>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Note</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff', color: rankColor(item.rank) }}>
              <td style={styles.td}>{item.portfolio}</td>
              <td style={styles.td}><strong>{item.ticker}</strong></td>
              <td style={{ ...styles.td, textAlign: 'center' }}>{item.rank}</td>
              <td style={styles.td}>{item.noteDate || '-'}</td>
              <td style={{ ...styles.td, maxWidth: '30em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.note ? item.note.substring(0, 80) + (item.note.length > 80 ? '...' : '') : '-'}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                <a href="#v" onClick={e => { e.preventDefault(); setViewId(viewId === item.id ? null : item.id); }} style={styles.actionLink}>View</a>
                {' | '}
                <a href="#e" onClick={e => { e.preventDefault(); startEdit(item); }} style={styles.actionLink}>Edit</a>
                {' | '}
                <a href="#d" onClick={e => { e.preventDefault(); handleDelete(item.id); }} style={styles.actionLink}>Delete</a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Ticker Mapper Tab ---- */
const TickerMapperTab: React.FC = () => {
  const [items, setItems] = useState<Tickermapper[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ ca_ticker: '', ca_exchange: '', us_ticker: '', us_exchange: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ ca_ticker: '', ca_exchange: '', us_ticker: '', us_exchange: '' });

  const load = () => {
    setLoading(true);
    tickermapperApi.list().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: Tickermapper) => {
    setEditId(item.id);
    setEditForm({ ca_ticker: item.ca_ticker, ca_exchange: item.ca_exchange, us_ticker: item.us_ticker, us_exchange: item.us_exchange });
  };
  const cancelEdit = () => setEditId(null);
  const saveEdit = async () => {
    if (editId === null) return;
    await tickermapperApi.update(editId, editForm);
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!addForm.ca_ticker.trim() || !addForm.us_ticker.trim()) return;
    await tickermapperApi.create(addForm);
    setAddForm({ ca_ticker: '', ca_exchange: '', us_ticker: '', us_exchange: '' });
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this mapping?')) return;
    await tickermapperApi.delete(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Mapping" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>CA Ticker:</label>
          <input value={addForm.ca_ticker} onChange={e => setAddForm({ ...addForm, ca_ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '7em' }} autoFocus />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>CA Exchange:</label>
          <input value={addForm.ca_exchange} onChange={e => setAddForm({ ...addForm, ca_exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '7em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>US Ticker:</label>
          <input value={addForm.us_ticker} onChange={e => setAddForm({ ...addForm, us_ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '7em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>US Exchange:</label>
          <input value={addForm.us_exchange} onChange={e => setAddForm({ ...addForm, us_exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '7em' }} />
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setAddForm({ ca_ticker: '', ca_exchange: '', us_ticker: '', us_exchange: '' }); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={{ ...styles.table, width: '70%' }}>
        <thead>
          <tr>
            <th style={styles.th}>CA Ticker</th>
            <th style={styles.th}>CA Exchange</th>
            <th style={styles.th}>US Ticker</th>
            <th style={styles.th}>US Exchange</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
              <td style={styles.td}>
                {editId === item.id ? <input value={editForm.ca_ticker} onChange={e => setEditForm({ ...editForm, ca_ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '100%' }} autoFocus /> : item.ca_ticker}
              </td>
              <td style={styles.td}>
                {editId === item.id ? <input value={editForm.ca_exchange} onChange={e => setEditForm({ ...editForm, ca_exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '100%' }} /> : item.ca_exchange}
              </td>
              <td style={styles.td}>
                {editId === item.id ? <input value={editForm.us_ticker} onChange={e => setEditForm({ ...editForm, us_ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '100%' }} /> : item.us_ticker}
              </td>
              <td style={styles.td}>
                {editId === item.id ? <input value={editForm.us_exchange} onChange={e => setEditForm({ ...editForm, us_exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '100%' }} /> : item.us_exchange}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                {editId === item.id ? (
                  <>
                    <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                    {' | '}
                    <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                  </>
                ) : (
                  <>
                    <a href="#e" onClick={e => { e.preventDefault(); startEdit(item); }} style={styles.actionLink}>Edit</a>
                    {' | '}
                    <a href="#d" onClick={e => { e.preventDefault(); handleDelete(item.id); }} style={styles.actionLink}>Delete</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Industry Mapping Tab ---- */
const IndustryMappingTab: React.FC = () => {
  const [items, setItems] = useState<IndustryMapper[]>([]);
  const [industries, setIndustries] = useState<IndustryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ industryID: 0, exchange: '', ticker: '', percentage: 100 });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ industryID: 0, exchange: '', ticker: '', percentage: 100 });

  const load = () => {
    setLoading(true);
    Promise.all([industryMapperApi.list(), configApi.industries()])
      .then(([r1, r2]) => {
        setItems(r1.data);
        setIndustries(r2.data.map((i: any) => ({ id: i.id, name: i.name })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: IndustryMapper) => {
    setEditId(item.id);
    setEditForm({ industryID: item.industryID, exchange: item.exchange, ticker: item.ticker, percentage: item.percentage });
  };
  const cancelEdit = () => setEditId(null);
  const saveEdit = async () => {
    if (editId === null) return;
    await industryMapperApi.update(editId, editForm);
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!addForm.ticker.trim() || !addForm.industryID) return;
    await industryMapperApi.create(addForm);
    setAddForm({ industryID: 0, exchange: '', ticker: '', percentage: 100 });
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this mapping?')) return;
    await industryMapperApi.delete(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Mapping" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>Industry:</label>
          <select value={addForm.industryID} onChange={e => setAddForm({ ...addForm, industryID: parseInt(e.target.value) || 0 })} style={styles.select}>
            <option value={0}>-- select --</option>
            {industries.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Exchange:</label>
          <input value={addForm.exchange} onChange={e => setAddForm({ ...addForm, exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '7em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Ticker:</label>
          <input value={addForm.ticker} onChange={e => setAddForm({ ...addForm, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '7em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>%:</label>
          <input type="number" value={addForm.percentage} onChange={e => setAddForm({ ...addForm, percentage: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '4em' }} />
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setAddForm({ industryID: 0, exchange: '', ticker: '', percentage: 100 }); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={{ ...styles.table, width: '70%' }}>
        <thead>
          <tr>
            <th style={styles.th}>Industry</th>
            <th style={styles.th}>Exchange</th>
            <th style={styles.th}>Ticker</th>
            <th style={styles.th}>Percentage</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
              <td style={styles.td}>
                {editId === item.id ? (
                  <select value={editForm.industryID} onChange={e => setEditForm({ ...editForm, industryID: parseInt(e.target.value) || 0 })} style={styles.select}>
                    {industries.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                ) : item.industryName}
              </td>
              <td style={styles.td}>
                {editId === item.id ? <input value={editForm.exchange} onChange={e => setEditForm({ ...editForm, exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '100%' }} /> : item.exchange}
              </td>
              <td style={styles.td}>
                {editId === item.id ? <input value={editForm.ticker} onChange={e => setEditForm({ ...editForm, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '100%' }} /> : <strong>{item.ticker}</strong>}
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                {editId === item.id ? <input type="number" value={editForm.percentage} onChange={e => setEditForm({ ...editForm, percentage: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '4em' }} /> : `${item.percentage}%`}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', whiteSpace: 'nowrap' }}>
                {editId === item.id ? (
                  <>
                    <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                    {' | '}
                    <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                  </>
                ) : (
                  <>
                    <a href="#e" onClick={e => { e.preventDefault(); startEdit(item); }} style={styles.actionLink}>Edit</a>
                    {' | '}
                    <a href="#d" onClick={e => { e.preventDefault(); handleDelete(item.id); }} style={styles.actionLink}>Delete</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- After-Hours Price Movers ---- */
interface AfterHoursItem {
  ticker: string; exchange: string; name: string;
  regularPrice: number; prevClose: number; dayChange: number; dayChangePct: number;
  postMarketPrice: number | null; postMarketChange: number | null; postMarketChangePct: number | null;
  preMarketPrice: number | null; preMarketChange: number | null; preMarketChangePct: number | null;
}

const AfterHoursPanel: React.FC = () => {
  const [items, setItems] = useState<AfterHoursItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    transactionApi.afterHours()
      .then((res) => {
        setItems(res.data.items);
        setLastUpdate(new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York' }));
      })
      .catch(() => setError('Failed to load after-hours data'))
      .finally(() => setLoading(false));
  };

  const color = (val: number | null) => val == null ? '#999' : val > 0 ? 'green' : val < 0 ? 'red' : '#333';
  const fmt = (val: number | null) => val == null ? '—' : (val > 0 ? '+' : '') + val.toFixed(2);
  const fmtPct = (val: number | null) => val == null ? '—' : (val > 0 ? '+' : '') + val.toFixed(2) + '%';

  const yahooUrl = (ticker: string, exchange: string) => {
    const suffix = exchange === 'TSE' ? '.TO' : exchange === 'CVE' ? '.V' : '';
    return `https://finance.yahoo.com/quote/${ticker}${suffix}`;
  };

  return (
    <div style={{ marginBottom: '1em' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1em', marginBottom: '0.5em' }}>
        <h4 style={{ color: '#0069cc', margin: 0 }}>Portfolio Extended Hours</h4>
        <input type="button" value={loading ? 'Loading...' : 'Refresh'} onClick={load} disabled={loading} style={styles.button} />
        {lastUpdate && <span style={{ fontSize: '0.8em', color: '#666' }}>Last updated: {lastUpdate} ET</span>}
      </div>
      {error && <div style={{ color: 'red', fontSize: '0.85em' }}>{error}</div>}
      {items.length > 0 && (
        <table style={{ ...styles.table, width: 'auto' }}>
          <thead>
            <tr>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Company</th>
              <th style={styles.th}>Close</th>
              <th style={styles.th}>Day Chg</th>
              <th style={styles.th}>Day %</th>
              <th style={styles.th}>After-Hrs</th>
              <th style={styles.th}>AH Chg</th>
              <th style={styles.th}>AH %</th>
              <th style={styles.th}>Pre-Mkt</th>
              <th style={styles.th}>PM Chg</th>
              <th style={styles.th}>PM %</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.ticker} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                <td style={{ ...styles.td, fontWeight: 'bold' }}>
                  <a href={yahooUrl(item.ticker, item.exchange)} target="_blank" rel="noreferrer"
                    style={{ color: '#0069cc', textDecoration: 'none', fontWeight: 'bold' }}>{item.ticker}</a>
                </td>
                <td style={{ ...styles.td, fontSize: '0.75em', maxWidth: '15em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{item.regularPrice.toFixed(2)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: color(item.dayChange) }}>{fmt(item.dayChange)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: color(item.dayChangePct), fontWeight: 'bold' }}>{fmtPct(item.dayChangePct)}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{item.postMarketPrice?.toFixed(2) ?? '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: color(item.postMarketChange) }}>{fmt(item.postMarketChange)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: color(item.postMarketChangePct), fontWeight: 'bold' }}>{fmtPct(item.postMarketChangePct)}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{item.preMarketPrice?.toFixed(2) ?? '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: color(item.preMarketChange) }}>{fmt(item.preMarketChange)}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: color(item.preMarketChangePct), fontWeight: 'bold' }}>{fmtPct(item.preMarketChangePct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ---- X Actions Panel ---- */
const XActionsPanel: React.FC = () => {
  const [likingNotifs, setLikingNotifs] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const [notifMax, setNotifMax] = useState(50);

  const handleLikeNotifications = async () => {
    if (!window.confirm(`Like up to ${notifMax} replies in your X notifications?`)) return;
    setLikingNotifs(true);
    setNotifMsg('');
    try {
      await form4Api.likeNotifications(notifMax);
      setNotifMsg('Queued! Liking notifications in background...');
    } catch (err: any) {
      setNotifMsg(`Error: ${err.message}`);
    } finally {
      setLikingNotifs(false);
    }
  };

  return (
    <div style={{ padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', fontSize: '0.85em', marginBottom: '1em' }}>
      <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>X Actions:</strong>
        <label>Max likes:</label>
        <input type="number" value={notifMax} onChange={(e) => setNotifMax(Number(e.target.value))}
          style={{ ...styles.input, width: '5em' }} min={1} max={200} />
        <input type="button" value={likingNotifs ? 'Queuing...' : 'Like Notifications'}
          onClick={handleLikeNotifications} disabled={likingNotifs}
          style={{ ...styles.button, backgroundColor: '#e8d0ff', fontWeight: 'bold' }} />
        {notifMsg && <span style={{ color: notifMsg.startsWith('Error') ? 'red' : '#2e7d32' }}>{notifMsg}</span>}
      </div>
    </div>
  );
};

/* ---- Main Component ---- */
const HomePage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('none');

  const allTabs: { key: TabKey; label: string; to?: string }[] = [
    { key: 'none', label: 'Portfolios', to: '/portfolios' },
    { key: 'none', label: 'Holdings', to: '/holdings' },
    { key: 'none', label: 'Monitoring', to: '/transactions' },
    { key: 'none', label: 'Users', to: '/users' },
    { key: 'none', label: 'Form 4 Transactions', to: '/form4' },
    { key: 'none', label: 'SEDI Transactions', to: '/sedi' },
    { key: 'none', label: 'Insider Activity Scoring', to: '/insider-activity' },
    { key: 'none', label: 'Institutional Ownership (13F)', to: '/institutions' },
    { key: 'none', label: 'Insider Ownership (SEDI)', to: '/insiders' },
    { key: 'notes', label: 'Stock Notes' },
    { key: 'tickermap', label: 'Ticker Mapper' },
    { key: 'industrymap', label: 'Industry Mapping' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.tabs}>
        {allTabs.map((t) =>
          t.to ? (
            <Link key={t.label} to={t.to} style={styles.tab}>
              {t.label}
            </Link>
          ) : (
            <span
              key={t.key}
              onClick={() => setTab(tab === t.key ? 'none' : t.key)}
              style={{ ...styles.tab, ...(tab === t.key ? styles.activeTab : {}) }}
            >
              {t.label}
            </span>
          )
        )}
      </div>

      <AfterHoursPanel />
      <XActionsPanel />

      {tab === 'notes' && <StockNotesTab />}
      {tab === 'tickermap' && <TickerMapperTab />}
      {tab === 'industrymap' && <IndustryMappingTab />}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  tabs: { display: 'flex', flexWrap: 'wrap' as const, gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tab: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px', color: '#1a1a1a', textDecoration: 'none' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  formWrapper: { maxWidth: '44em', margin: '0.5em auto 1em' },
  fieldset: { border: '1px solid #808080', backgroundColor: '#fff', padding: '0.8em 1em 0' },
  legend: { fontWeight: 'bold', marginLeft: '0.5em', padding: '0 0.2em' },
  fieldList: { listStyle: 'none', padding: '0.5em 1em 0', margin: 0 },
  field: { paddingBottom: '0.6em', display: 'flex', alignItems: 'flex-start' },
  formLabel: { width: '7em', textAlign: 'right' as const, marginRight: '1em', paddingTop: '3px' },
  addRow: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '0.5em', display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: '4px' },
  addLabel: { fontWeight: 'bold' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  select: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default HomePage;
